import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Input } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';

interface Message {
  id: string;
  from: string;
  email: string;
  subject: string;
  message: string;
  language: 'en' | 'ar';
  status: 'read' | 'unread';
  priority: 'high' | 'medium' | 'low';
  category: 'inquiry' | 'complaint' | 'order' | 'general';
  createdAt: string;
  readAt?: string;
}

const Messages: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  // Mock data
  useEffect(() => {
    const mockMessages: Message[] = [
      {
        id: '1',
        from: 'أحمد محمد',
        email: 'ahmed.mohamed@email.com',
        subject: 'استفسار عن دواء السكري',
        message: 'السلام عليكم، أريد الاستفسار عن دواء الميتفورمين، هل يتوفر في الصيدلية؟ وما هو السعر؟ شكراً لكم.',
        language: 'ar',
        status: 'unread',
        priority: 'medium',
        category: 'inquiry',
        createdAt: '2024-10-04T10:30:00Z'
      },
      {
        id: '2',
        from: 'Sarah Johnson',
        email: 'sarah.johnson@email.com',
        subject: 'Question about vitamin D supplements',
        message: 'Hello, I would like to know if you have vitamin D3 supplements in stock. Also, what dosage would you recommend for adults? Thank you.',
        language: 'en',
        status: 'read',
        priority: 'low',
        category: 'inquiry',
        createdAt: '2024-10-03T14:15:00Z',
        readAt: '2024-10-03T15:30:00Z'
      },
      {
        id: '3',
        from: 'فاطمة علي',
        email: 'fatima.ali@email.com',
        subject: 'شكوى بخصوص الطلب رقم 12345',
        message: 'لدي شكوى بخصوص الطلب رقم 12345. لقد وصل الدواء متأخراً وكان التغليف تالفاً. أرجو حل هذه المشكلة في أسرع وقت ممكن.',
        language: 'ar',
        status: 'unread',
        priority: 'high',
        category: 'complaint',
        createdAt: '2024-10-02T09:45:00Z'
      },
      {
        id: '4',
        from: 'Michael Chen',
        email: 'michael.chen@email.com',
        subject: 'Order status inquiry',
        message: 'Hi, I placed an order yesterday (Order #98765) and I haven\'t received any confirmation email yet. Could you please check the status? Thanks.',
        language: 'en',
        status: 'read',
        priority: 'medium',
        category: 'order',
        createdAt: '2024-10-01T16:20:00Z',
        readAt: '2024-10-01T17:45:00Z'
      },
      {
        id: '5',
        from: 'خالد الأحمد',
        email: 'khalid.ahmed@email.com',
        subject: 'معلومات عامة عن الصيدلية',
        message: 'مرحباً، أريد معرفة ساعات العمل في الصيدلية خلال نهاية الأسبوع، وهل تقدمون خدمة التوصيل المنزلي؟',
        language: 'ar',
        status: 'unread',
        priority: 'low',
        category: 'general',
        createdAt: '2024-09-30T11:10:00Z'
      }
    ];

    setTimeout(() => {
      setMessages(mockMessages);
      setLoading(false);
    }, 1000);
  }, []);

  const filteredMessages = messages.filter(message => {
    const matchesSearch =
      message.from.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || message.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleSelectMessage = (messageId: string) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedMessages.size === filteredMessages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(filteredMessages.map(m => m.id)));
    }
  };

  const handleMarkAsRead = (messageId: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId
        ? { ...msg, status: 'read' as const, readAt: new Date().toISOString() }
        : msg
    ));
    addToast({ title: t('messages.messageMarkedRead'), message: '', type: 'success' });
  };

  const handleMarkAsUnread = (messageId: string) => {
    setMessages(prev => prev.map(msg =>
      msg.id === messageId
        ? { ...msg, status: 'unread' as const, readAt: undefined }
        : msg
    ));
    addToast({ title: t('messages.messageMarkedUnread'), message: '', type: 'success' });
  };

  const handleDeleteMessage = (messageId: string) => {
    if (window.confirm(t('messages.deleteConfirm'))) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      addToast({ title: t('messages.messageDeleted'), message: '', type: 'success' });
    }
  };

  const handleBulkMarkAsRead = () => {
    setMessages(prev => prev.map(msg =>
      selectedMessages.has(msg.id)
        ? { ...msg, status: 'read' as const, readAt: new Date().toISOString() }
        : msg
    ));
    setSelectedMessages(new Set());
    addToast({ title: t('messages.bulkMarkedRead'), message: '', type: 'success' });
  };

  const handleBulkDelete = () => {
    if (window.confirm(t('messages.bulkDeleteConfirm'))) {
      setMessages(prev => prev.filter(m => !selectedMessages.has(m.id)));
      setSelectedMessages(new Set());
      addToast({ title: t('messages.bulkDeleted'), message: '', type: 'success' });
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'priority-high';
      case 'medium':
        return 'priority-medium';
      case 'low':
        return 'priority-low';
      default:
        return 'priority-low';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">{t('navigation.messages')}</h1>
        </div>
        <div className="page-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">{t('navigation.messages')}</h1>
        <div className="messages-stats">
          <span className="stat">
            {messages.filter(m => m.status === 'unread').length} {t('messages.unread')}
          </span>
          <span className="stat">
            {messages.length} {t('messages.total')}
          </span>
        </div>
      </div>

      <div className="page-content">
        <Card className="filters-card">
          <div className="filters-row">
            <div className="filter-group">
              <Input
                placeholder={t('messages.searchPlaceholder')}
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            <div className="filter-group">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="status-select"
              >
                <option value="all">{t('messages.allMessages')}</option>
                <option value="unread">{t('messages.unread')}</option>
                <option value="read">{t('messages.read')}</option>
              </select>
            </div>
          </div>

          {selectedMessages.size > 0 && (
            <div className="bulk-actions">
              <span className="selected-count">
                {selectedMessages.size} {t('messages.selected')}
              </span>
              <div className="bulk-buttons">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleBulkMarkAsRead}
                >
                  {t('messages.markAsRead')}
                </Button>
                <Button
                  variant="error"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  {t('common.delete')}
                </Button>
              </div>
            </div>
          )}
        </Card>

        <div className="messages-container">
          <div className="messages-list">
            <div className="messages-header">
              <label className="select-all">
                <input
                  type="checkbox"
                  checked={selectedMessages.size === filteredMessages.length && filteredMessages.length > 0}
                  onChange={handleSelectAll}
                />
                <span>{t('messages.selectAll')}</span>
              </label>
            </div>

            {filteredMessages.map(message => (
              <div
                key={message.id}
                className={`message-item ${message.status} ${selectedMessages.has(message.id) ? 'selected' : ''}`}
                dir={message.language === 'ar' ? 'rtl' : 'ltr'}
              >
                <div className="message-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedMessages.has(message.id)}
                    onChange={() => handleSelectMessage(message.id)}
                  />
                </div>

                <div className={`priority-indicator ${getPriorityColor(message.priority)}`}></div>

                <div className="message-content" onClick={() => setSelectedMessage(message)}>
                  <div className="message-header">
                    <div className="message-info">
                      <span className="sender-name">{message.from}</span>
                      <span className="sender-email">{message.email}</span>
                      <span className={`category-tag ${message.category}`}>
                        {t(`messages.category.${message.category}`)}
                      </span>
                    </div>
                    <div className="message-meta">
                      <span className="message-date">{formatDate(message.createdAt)}</span>
                      {message.status === 'unread' && (
                        <span className="unread-indicator"></span>
                      )}
                    </div>
                  </div>

                  <div className="message-subject">{message.subject}</div>
                  <div className="message-preview">{message.message.substring(0, 120)}...</div>
                </div>

                <div className="message-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => message.status === 'read'
                      ? handleMarkAsUnread(message.id)
                      : handleMarkAsRead(message.id)
                    }
                  >
                    {message.status === 'read' ? t('messages.markAsUnread') : t('messages.markAsRead')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => console.log('Reply to message', message.id)}
                  >
                    {t('messages.reply')}
                  </Button>
                  <Button
                    variant="error"
                    size="sm"
                    onClick={() => handleDeleteMessage(message.id)}
                  >
                    {t('common.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {selectedMessage && (
            <div className="message-detail" dir={selectedMessage.language === 'ar' ? 'rtl' : 'ltr'}>
              <div className="message-detail-header">
                <h3>{selectedMessage.subject}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedMessage(null)}
                >
                  ✕
                </Button>
              </div>
              <div className="message-detail-meta">
                <div className="sender-info">
                  <strong>{selectedMessage.from}</strong>
                  <span>{selectedMessage.email}</span>
                </div>
                <div className="message-detail-date">
                  {formatDate(selectedMessage.createdAt)}
                </div>
              </div>
              <div className="message-detail-content">
                {selectedMessage.message}
              </div>
              <div className="message-detail-actions">
                <Button variant="primary" onClick={() => console.log('Reply')}>
                  {t('messages.reply')}
                </Button>
                <Button variant="secondary" onClick={() => console.log('Forward')}>
                  {t('messages.forward')}
                </Button>
              </div>
            </div>
          )}
        </div>

        {filteredMessages.length === 0 && (
          <div className="empty-state">
            <p>{t('messages.noMessages')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;